import React, { useState, useEffect } from 'react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Label } from './components/ui/label';
import { RadioGroup, RadioGroupItem } from './components/ui/radio-group';
import { Checkbox } from './components/ui/checkbox';
import { Toaster, toast } from 'sonner';
import { BookOpen, MapPin } from 'lucide-react';
import GrantSpiralOrb from './components/GrantSpiralOrb';
import MarshallAudioPlayer from './components/MarshallAudioPlayer';
import PremiumFooter from './components/PremiumFooter';
import rosenbergQuotes from './data/rosenberg_quotes.json';
import './App.css';

function App() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [waitingListDialogOpen, setWaitingListDialogOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registeredName, setRegisteredName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [seatInfo, setSeatInfo] = useState({ 
    intro_available: 30, 
    series_available: 15, 
    intro_percent_filled: 0, 
    series_percent_filled: 0,
    intro_waiting_list_enabled: false,
    series_waiting_list_enabled: false,
    intro_full: false,
    series_full: false
  });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    attending: 'full_series',
    price_selected: '175',
    referral: '',
    in_person: false,
    comments: ''
  });
  const [waitingListData, setWaitingListData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    attending: 'full_series'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Fetch seat availability
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/settings`)
      .then(res => res.json())
      .then(data => setSeatInfo(data))
      .catch(err => console.error('Failed to fetch seat info'));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/registrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      // BULLETPROOF FIX: Read body ONLY ONCE as text, then parse
      // This completely eliminates "body is disturbed or locked" errors
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error, raw response:', responseText);
        throw new Error('Server returned invalid response');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      
      // Success - save user info and show password modal
      const full_name = `${formData.first_name} ${formData.last_name}`;
      setRegisteredEmail(formData.email);
      setRegisteredName(full_name);
      
      // Close registration dialog and open password modal
      setDialogOpen(false);
      setPasswordModalOpen(true);
      
      // Clear form
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        attending: 'full_series',
        price_selected: '175',
        referral: '',
        in_person: false,
        comments: ''
      });
      
      toast.success('Registration successful! Please create your password.');
      setIsSubmitting(false);
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(`Registration failed: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/activate-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: registeredEmail,
          password: newPassword
        })
      });
      
      // BULLETPROOF FIX: Read body ONLY ONCE as text, then parse
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error, raw response:', responseText);
        throw new Error('Server returned invalid response');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to set password');
      }
      
      // Save user data
      localStorage.setItem('workbook_user_id', data.user_id);
      localStorage.setItem('workbook_user_name', data.name);
      localStorage.setItem('workbook_user_email', registeredEmail);
      
      // Close modal and redirect
      setPasswordModalOpen(false);
      setNewPassword('');
      setConfirmPassword('');
      
      toast.success('Account activated! Redirecting to your dashboard...');
      
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);
      
    } catch (error) {
      setPasswordError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWaitingListSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waiting-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(waitingListData)
      });

      if (!response.ok) throw new Error('Failed to join waiting list');

      toast.success('You\'ve been added to the waiting list! We\'ll contact you when seats open up.');
      
      setWaitingListDialogOpen(false);
      setWaitingListData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        attending: 'full_series'
      });
    } catch (error) {
      toast.error('Failed to join waiting list. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const email = formData.get('email');
    const name = formData.get('name');
    const phone = formData.get('phone');
    const interest = formData.get('interest');
    const comments = formData.get('comments');
    const contactPreference = formData.get('contactPreference');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          name, 
          phone,
          interest,
          comments,
          contactPreference,
          type: 'contact_form' 
        })
      });
      
      if (!response.ok) throw new Error('Failed');
      toast.success("Thank you! We'll be in touch.");
      e.target.reset();
    } catch (error) {
      toast.error('Please try again');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      
      {/* Password Creation Modal */}
      <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
        <DialogContent className="max-w-md" style={{ borderRadius: 0 }}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              Create Your Password
            </DialogTitle>
          </DialogHeader>
          <div className="text-center mb-4">
            <p className="text-gray-600">Welcome, <strong>{registeredName}</strong>!</p>
            <p className="text-sm text-gray-500 mt-1">Please create a secure password for your account.</p>
          </div>
          <form onSubmit={handleSetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="border-black"
                style={{ borderRadius: 0 }}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm Password *</Label>
              <Input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                className="border-black"
                style={{ borderRadius: 0 }}
                data-testid="confirm-password-input"
              />
            </div>
            {passwordError && (
              <p className="text-red-600 text-sm text-center">{passwordError}</p>
            )}
            <Button
              type="submit"
              className="w-full bg-black text-white hover:bg-black/90 py-4"
              style={{ borderRadius: 0 }}
              disabled={isSubmitting}
              data-testid="set-password-btn"
            >
              {isSubmitting ? 'Creating Account...' : 'Activate My Account'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Full-Screen Hero */}
      <section 
        className="relative h-screen flex items-center justify-center"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_nvc-workbook/artifacts/zivwk218_IMG_3186.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 text-center px-4 max-w-5xl">
          <p className="text-2xl md:text-3xl font-light text-white mb-4" style={{ letterSpacing: '0.1em', opacity: 0.9 }}>
            THE BELONGING SESSIONS
          </p>
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-8" style={{ letterSpacing: '-0.04em' }}>
            Learn the Language<br/>of Belonging
          </h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                size="lg"
                className="bg-white text-black hover:bg-white/90 px-12 py-8 text-xl font-semibold"
                style={{ borderRadius: 0 }}
                data-testid="register-btn"
                onClick={() => setFormData(prev => ({ ...prev, attending: 'full_series' }))}
              >
                Reserve Your Seat
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" style={{ borderRadius: 0 }}>
              <DialogHeader>
                <DialogTitle className="text-2xl md:text-3xl font-bold">Register for Eight-Session Series</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6 mt-4 md:mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">First Name *</Label>
                    <Input
                      required
                      value={formData.first_name || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      className="border-black"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Last Name *</Label>
                    <Input
                      required
                      value={formData.last_name || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      className="border-black"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Email *</Label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="border-black"
                    style={{ borderRadius: 0 }}
                    data-testid="registration-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Phone Number *</Label>
                  <Input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="border-black"
                    style={{ borderRadius: 0 }}
                    data-testid="registration-phone"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Comments (optional)</Label>
                  <Textarea
                    value={formData.comments}
                    onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                    placeholder="Any questions or special requests?"
                    className="border-black min-h-[60px] md:min-h-[80px]"
                    style={{ borderRadius: 0 }}
                  />
                </div>

                <div className="p-3 md:p-4 bg-blue-50 border-l-4 border-blue-600">
                  <p className="font-semibold text-sm md:text-base mb-1 md:mb-2">Eight-Session Series</p>
                  <p className="text-xs md:text-sm text-gray-700 mb-2">Thursdays at 12:00 PM CT (noon) • 2 hours each</p>
                  <div className="text-xs text-gray-600 space-y-0.5">
                    <p>Session 1: Dec 4 • Session 2: Dec 18</p>
                    <p>Session 3: Jan 8 • Session 4: Jan 22</p>
                    <p>Session 5: Feb 5 • Session 6: Feb 19</p>
                    <p>Session 7: Mar 5 • Session 8: Mar 19</p>
                  </div>
                </div>

                <div className="space-y-2 md:space-y-3">
                  <Label className="text-sm md:text-base">Investment</Label>
                  <RadioGroup 
                    value={formData.price_selected} 
                    onValueChange={(value) => {
                      if (value === 'newsletter') {
                        setFormData(prev => ({ ...prev, price_selected: value, attending: 'newsletter' }));
                      } else {
                        setFormData(prev => ({ ...prev, price_selected: value, attending: 'full_series' }));
                      }
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="newsletter" id="newsletter" data-testid="radio-newsletter" />
                      <Label htmlFor="newsletter" className="font-normal cursor-pointer text-sm md:text-base">📧 Just sign me up for newsletter (not ready to book course)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="175" id="price-175" data-testid="radio-price-175" />
                      <Label htmlFor="price-175" className="font-normal cursor-pointer text-sm md:text-base">$175 (standard tuition)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="scholarship" id="scholarship" data-testid="radio-scholarship" />
                      <Label htmlFor="scholarship" className="font-normal cursor-pointer text-sm md:text-base">I request a scholarship (we'll contact you)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="pay_forward" id="pay-forward" data-testid="radio-pay-forward" />
                      <Label htmlFor="pay-forward" className="font-normal cursor-pointer text-sm md:text-base">I can pay more / pay it forward</Label>
                    </div>
                  </RadioGroup>
                  <p className="text-xs md:text-sm text-gray-600">
                    Choose your investment in compassionate connection and community.
                  </p>
                </div>

                <div className="flex items-center space-x-3 p-3 md:p-4 bg-gray-50 rounded">
                  <Checkbox
                    id="in-person"
                    checked={formData.in_person}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, in_person: checked }))}
                  />
                  <Label htmlFor="in-person" className="font-normal cursor-pointer text-sm md:text-base">
                    I would like to attend IN-PERSON at Defiance Springs (Viroqua, WI) 
                    <span className="text-xs md:text-sm text-gray-600 block mt-1">Limited in-person seating available</span>
                  </Label>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-black text-white hover:bg-black/90 py-4 md:py-6 text-base md:text-lg"
                  disabled={isSubmitting}
                  style={{ borderRadius: 0 }}
                >
                  {isSubmitting ? 'Submitting...' : 'Complete Registration'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Learn the Language of Belonging */}
      <section className="py-32 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl md:text-7xl font-bold mb-8">Learn the Language of Belonging</h2>
          <p className="text-3xl md:text-4xl font-light mb-4">A Hybrid Eight-Part Journey</p>
          <p className="text-2xl text-gray-600 mb-16">Presented by Defiance Springs</p>
          
          <div className="max-w-4xl mx-auto">
            <p className="text-xl leading-relaxed text-gray-700">
              This is a hybrid event — join live on Zoom from the comfort of your home or in person at Defiance Springs Lodge in Viroqua, Wisconsin.
            </p>
          </div>
          
          {/* User Login Link */}
          <div className="mt-12 flex justify-center">
            <a 
              href="/login"
              className="inline-block px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-full hover:from-blue-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl"
            >
              👤 Member Login
            </a>
          </div>
        </div>
      </section>

      {/* What If */}
      <section className="py-32 px-4" style={{ backgroundColor: 'hsl(138 25% 96%)' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-bold mb-12 text-center">What If</h2>
          <div className="max-w-5xl mx-auto text-center">
            <p className="text-2xl md:text-3xl leading-relaxed text-gray-700 mb-8">
              What if our language itself could become a healing art — a way to build bridges where the world has built walls?
            </p>
            <div className="space-y-6 text-xl leading-relaxed text-gray-700">
              <p>
                Guided by <strong>Steve Torma</strong> — teacher, storyteller, and co-founder of The REAL Center — The Belonging Sessions invite you to learn and practice the art of compassionate communication.
              </p>
              <p>
                For more than forty years, Steve has explored what it means to live compassionately in community. A longtime resident and former president of Earthaven Ecovillage, he weaves together Nonviolent Communication, Radical Honesty, Restorative Circles, Creation Spirituality, and Permaculture into an embodied way of being that helps us remember connection as our natural state.
              </p>
              <p>
                A new story is calling us forward — what author Riane Eisler, in <em>The Chalice and the Blade</em>, names partnership culture: a way of living rooted in empathy, mutual care, and shared power.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Together We'll Explore */}
      <section className="py-32 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-16">Together, we'll explore how to:</h2>
          <div className="max-w-6xl mx-auto">
            <div className="text-xl leading-relaxed text-gray-700 space-y-4">
              <p>listen beneath words — to feelings, needs, and what's alive in one another;</p>
              <p>speak truth without blame or fear;</p>
              <p>bring compassion to conflict and curiosity to difference.</p>
            </div>
          </div>
          <div className="mt-16 max-w-4xl mx-auto">
            <p className="text-2xl italic text-gray-700 font-bold">
              When communication becomes a bridge, relationship becomes the medicine.
            </p>
          </div>
        </div>
      </section>

      {/* Dates & Times */}
      <section className="py-32 px-4" style={{ backgroundColor: 'hsl(138 25% 96%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-5xl md:text-7xl font-bold">Dates & Times</h2>
            <a href="/directions">
              <Button 
                variant="outline"
                className="border-black flex items-center gap-2"
                style={{ borderRadius: 0 }}
              >
                <MapPin className="w-4 h-4" />
                Directions
              </Button>
            </a>
          </div>
          <div className="text-center max-w-4xl mx-auto space-y-8">
            <p className="text-2xl text-gray-700">
              <strong>Thursdays at 12:00 PM CT (noon)</strong> — Each session 2 hours
            </p>
            <div className="text-xl text-gray-700">
              <p>Dec 4 • Dec 18 • Jan 8 • Jan 22 • Feb 5 • Feb 19 • Mar 5 • Mar 19</p>
            </div>
            <p className="text-lg text-gray-600">
              Sessions are spaced to allow time for reflection, integration, and practice with friends, family, and fellow travelers.
            </p>
          </div>
        </div>
      </section>

      {/* Tuition & Reciprocity */}
      <section className="py-32 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl md:text-7xl font-bold mb-12">Tuition & Reciprocity</h2>
          <div className="max-w-4xl mx-auto space-y-6">
            <p className="text-2xl text-gray-700">$175 – Standard Tuition</p>
            <p className="text-xl text-gray-700">Request Tuition Assistance</p>
            <p className="text-xl text-gray-700">Pay It Forward – contribute more to support others</p>
            <p className="text-2xl italic font-semibold mt-8" style={{ color: 'hsl(138 30% 40%)' }}>
              "Give what you can; receive what you need.<br/>
              No one turned away for financial reasons."
            </p>
          </div>
        </div>
      </section>

      {/* A Living Mandala of Wisdom */}
      <section className="py-32 px-4" style={{ backgroundColor: 'hsl(138 25% 96%)' }}>
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl md:text-7xl font-bold mb-12">A Living Mandala of Wisdom</h2>
          <div className="max-w-5xl mx-auto space-y-8 text-lg leading-relaxed text-gray-700">
            <p>Steve's teaching draws from lineages that remind us how to live in relationship — with self, others, and Earth:</p>
            
            <div className="grid md:grid-cols-1 gap-4 text-left max-w-3xl mx-auto">
              <p><strong>Marshall Rosenberg</strong> — Nonviolent Communication – empathy as a bridge between souls</p>
              <p><strong>Riane Eisler</strong> — The Chalice and the Blade – shifting from domination to partnership culture</p>
              <p><strong>Peter Levine</strong> — Somatic Experiencing – listening to the body's wisdom to restore trust</p>
              <p><strong>Susan Campbell</strong> — Getting Real – the courage to show up with radical presence</p>
              <p><strong>Brad Blanton</strong> — Radical Honesty – reclaiming truth as liberation</p>
              <p><strong>Eckhart Tolle</strong> — The Power of Now – awakening to presence in each moment</p>
            </div>
            
            <p className="text-xl italic mt-8">
              Together, these threads form a living mandala of compassion and awareness — an invitation to live with more courage, empathy, and authentic connection.
            </p>
          </div>
        </div>
      </section>

      {/* Our Shared Prayer */}
      <section className="py-32 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl md:text-7xl font-bold mb-12">Our Shared Prayer</h2>
          <div className="max-w-5xl mx-auto space-y-8 text-xl leading-relaxed text-gray-700">
            <p className="text-2xl italic font-semibold" style={{ color: 'hsl(138 30% 40%)' }}>
              "When we focus on clarifying what is being observed, felt, and needed rather than on diagnosing and judging, we discover the depth of our own compassion."
            </p>
            <p>
              Each time we speak with empathy, we remember the Language of Life — the pulse that connects all hearts.
            </p>
            <p>
              This series invites you to slow down, listen deeper, and speak from a place of truth and care — to rediscover what it means to be seen, to belong, and to build peace one conversation at a time.
            </p>
          </div>
          
          {/* Marshall's Audio - Small Version */}
          <div className="flex justify-center mt-12">
            <MarshallAudioPlayer size="small" />
          </div>
        </div>
      </section>

      {/* Defiance Springs: Our Intention */}
      <section className="py-32 px-4" style={{ backgroundColor: 'hsl(138 25% 96%)' }}>
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl md:text-7xl font-bold mb-12">Defiance Springs: Our Intention</h2>
          <div className="max-w-5xl mx-auto space-y-8 text-xl leading-relaxed text-gray-700">
            <p>
              At Defiance Springs, we envision families and communities grounded in empathy, courage, and connection — where communicating from the heart becomes a daily act of regeneration.
            </p>
            <p>
              Belonging means being part of a living network of trust — people doing their best for the greatest good, tending the long story that carries us forward:
            </p>
            <p className="text-2xl italic font-bold" style={{ color: 'hsl(138 30% 40%)' }}>
              A story that carries us, and those yet to come,<br/>
              10,000 generations into a thriving, compassionate partnership culture.
            </p>
            <div className="mt-12 p-8 bg-black text-white" style={{ borderRadius: 0 }}>
              <p className="text-xl italic">
                "We are in defiance of unnecessary suffering - from that springs endless abundance for the families of the planet. AND by relentlessly addressing conflict (when it arises) we build better belonging and with better belonging, we can build better!"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Register Now */}
      <section className="py-32 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl md:text-7xl font-bold mb-12">Register Now</h2>
          <p className="text-2xl text-gray-700 mb-12">Reserve your place in The Belonging Sessions</p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                size="lg"
                className="bg-black text-white hover:bg-gray-800 px-12 py-8 text-xl font-semibold"
                style={{ borderRadius: 0 }}
                onClick={() => setFormData(prev => ({ ...prev, attending: 'full_series' }))}
              >
                Reserve Your Seat
              </Button>
            </DialogTrigger>
          </Dialog>
          
          <div className="mt-12">
            <p className="text-lg text-gray-600">With love,</p>
            <p className="text-xl font-semibold text-gray-700">From Defiance Springs Team - Forest Revival Gatherings</p>
          </div>
        </div>
      </section>

      {/* bell hooks Quote */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div 
            className="relative overflow-hidden rounded-3xl p-10 md:p-14 text-center"
            style={{
              background: 'linear-gradient(135deg, rgba(88, 28, 135, 0.15) 0%, rgba(124, 58, 237, 0.1) 50%, rgba(167, 139, 250, 0.08) 100%)',
              border: '1px solid rgba(167, 139, 250, 0.3)',
              boxShadow: '0 20px 60px rgba(88, 28, 135, 0.15), inset 0 1px 0 rgba(255,255,255,0.1)'
            }}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-32 h-32 bg-purple-400/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-violet-400/10 rounded-full blur-3xl"></div>
            
            {/* Quote mark */}
            <div className="text-6xl text-purple-300/40 font-serif mb-4">"</div>
            
            <blockquote className="relative z-10">
              <p className="text-xl md:text-2xl lg:text-3xl text-gray-800 leading-relaxed font-light italic mb-8">
                I dreamed about a culture of belonging. I still dream that dream. I contemplate what our lives would be like if we knew how to cultivate awareness, to live mindfully, peacefully; if we learned habits of being that would bring us closer together, that would help us build beloved community.
              </p>
              <footer className="mt-6">
                <cite className="not-italic">
                  <span className="text-xl md:text-2xl font-semibold text-purple-900 block">bell hooks</span>
                  <span className="text-base md:text-lg text-purple-700/80 mt-1 block">Belonging: A Culture of Place</span>
                </cite>
              </footer>
            </blockquote>
            
            {/* Closing quote mark */}
            <div className="text-6xl text-purple-300/40 font-serif mt-4 rotate-180">"</div>
          </div>
        </div>
      </section>

      {/* Spacer - 1/4 page */}
      <div className="h-64"></div>

      {/* What We Will Giveth */}
      <section className="py-32 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">What We Will Giveth</h2>
          </div>

          {/* Grant Spiral Orb - Quote of the Day */}
          <div className="mb-16 flex justify-center">
            <GrantSpiralOrb 
              wisdom={rosenbergQuotes.quotes[Math.floor(Math.random() * rosenbergQuotes.quotes.length)].text}
              enabled={true}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div 
              className="p-10 text-center backdrop-blur-md transition-all hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
                animation: 'gentleFloat 6s ease-in-out infinite'
              }}
            >
              <h3 className="text-2xl font-bold mb-3">Interactive Sessions</h3>
              <p className="text-base text-gray-700 leading-relaxed">Multiple sharing and practice breakouts during sessions</p>
            </div>

            <div 
              className="p-10 text-center backdrop-blur-md transition-all hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
                animation: 'gentleFloat 6s ease-in-out infinite 1.5s'
              }}
            >
              <h3 className="text-2xl font-bold mb-3">Online Workbook & Journal</h3>
              <p className="text-base text-gray-700 leading-relaxed">For you to keep, practice, and revisit anytime</p>
            </div>

            <div 
              className="p-10 text-center backdrop-blur-md transition-all hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
                animation: 'gentleFloat 6s ease-in-out infinite 3s'
              }}
            >
              <h3 className="text-2xl font-bold mb-3">Endless Practice Scenarios</h3>
              <p className="text-base text-gray-700 leading-relaxed">Generated for your continued learning and growth</p>
            </div>

            <div 
              className="p-10 text-center backdrop-blur-md transition-all hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
                animation: 'gentleFloat 6s ease-in-out infinite 4.5s'
              }}
            >
              <h3 className="text-2xl font-bold mb-3">Family of Families</h3>
              <p className="text-base text-gray-700 leading-relaxed">Part of our growing Belonging Sessions community</p>
            </div>
          </div>

          {/* Free Communication App - Centered Feature */}
          <div className="mt-12 flex justify-center">
            <div 
              className="p-12 text-center max-w-2xl cursor-pointer transition-all hover:scale-105"
              onClick={() => window.open('/ofnr-trainer', '_blank')}
              style={{
                background: 'black',
                boxShadow: '12px 12px 0px rgba(0,0,0,0.3)'
              }}
            >
              <h3 className="text-3xl font-bold mb-4 text-white">Free Compassionate Communication App</h3>
              <p className="text-lg text-gray-300 mb-6">
                Interactive color-coded OFNR trainer • Available to everyone
              </p>
              <p className="text-xl text-white font-semibold">
                Click to Explore the Universal NVC Tool →
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with User Login */}
      <section className="py-32 px-4 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center space-y-12">
          <div className="space-y-6 max-w-3xl mx-auto">
            <p className="text-4xl md:text-5xl font-bold leading-tight">
              Belonging is the path of least resistance for self, friends, families, couples, teams, and communities to be highest functioning.
            </p>
            <p className="text-2xl text-gray-300">
              By eliminating unnecessary suffering and conflict, we can take our collaborations—for all our relations—to the next level.
            </p>
          </div>
          
          <div className="mt-8 flex flex-col items-center gap-6">
            <a href="/login">
              <Button 
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-white hover:bg-white hover:text-black px-12 py-6 text-lg font-semibold"
                style={{ borderRadius: 0 }}
              >
                User Login / Dashboard
              </Button>
            </a>
            <a href="/admin" className="text-white hover:underline text-sm">
              → Admin Login
            </a>
          </div>
        </div>
      </section>

      {/* Compact Contact & Newsletter Section */}
      <section className="py-12 px-4" style={{ backgroundColor: 'hsl(138 25% 96%)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Newsletter Signup - Left Side */}
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2">Stay Connected</h3>
              <p className="text-gray-600 text-sm mb-4">
                Get updates on sessions & community events
              </p>
              <form 
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const email = formData.get('mailing_email');
                  const name = formData.get('mailing_name');
                  
                  try {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact-requests`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email, name, type: 'mailing_list' })
                    });
                    if (!response.ok) throw new Error('Failed');
                    toast.success("You're on the list!");
                    e.target.reset();
                  } catch (error) {
                    toast.error('Please try again');
                  }
                }}
                className="space-y-3"
                data-testid="mailing-list-form"
              >
                <Input
                  type="text"
                  name="mailing_name"
                  placeholder="Your name"
                  className="border-gray-300 bg-white"
                  style={{ borderRadius: 0 }}
                  data-testid="mailing-name-input"
                />
                <Input
                  type="email"
                  name="mailing_email"
                  placeholder="Your email"
                  required
                  className="border-gray-300 bg-white"
                  style={{ borderRadius: 0 }}
                  data-testid="mailing-email-input"
                />
                <Button 
                  type="submit"
                  className="w-full bg-black text-white hover:bg-black/90"
                  style={{ borderRadius: 0 }}
                  data-testid="mailing-submit-btn"
                >
                  Subscribe to Newsletter
                </Button>
              </form>
            </div>
            
            {/* Contact Form - Right Side */}
            <div>
              <h3 className="text-2xl font-bold mb-2 text-center md:text-left">Send a Message</h3>
              <p className="text-gray-600 text-sm mb-4 text-center md:text-left">
                Questions? We'd love to hear from you
              </p>
              <form 
                onSubmit={handleContactSubmit}
                className="space-y-3"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    type="text"
                    name="name"
                    placeholder="Name *"
                    required
                    className="border-gray-300 bg-white"
                    style={{ borderRadius: 0 }}
                    data-testid="contact-name-input"
                  />
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email *"
                    required
                    className="border-gray-300 bg-white"
                    style={{ borderRadius: 0 }}
                    data-testid="contact-email-input"
                  />
                </div>
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Phone (optional)"
                  className="border-gray-300 bg-white"
                  style={{ borderRadius: 0 }}
                  data-testid="contact-phone-input"
                />
                <Textarea
                  name="comments"
                  placeholder="Your message..."
                  required
                  rows={3}
                  className="border-gray-300 bg-white resize-none"
                  style={{ borderRadius: 0 }}
                  data-testid="contact-message-input"
                />
                <input type="hidden" name="interest" value="general" />
                <input type="hidden" name="contactPreference" value="email" />
                <Button 
                  type="submit"
                  className="w-full bg-black text-white hover:bg-black/90"
                  style={{ borderRadius: 0 }}
                  data-testid="contact-submit-btn"
                >
                  Send Message
                </Button>
              </form>
            </div>
          </div>
          
          {/* CTA Button */}
          <div className="mt-8 text-center">
            <Button
              onClick={() => setDialogOpen(true)}
              className="px-8 py-5 text-base font-semibold text-white shadow-lg hover:scale-105 transition-all duration-300"
              style={{ 
                background: 'linear-gradient(135deg, #f59e0b, #d97706, #b45309)',
                borderRadius: '9999px'
              }}
              data-testid="footer-register-cta"
            >
              ✨ Join the Belonging Sessions ✨
            </Button>
          </div>
        </div>
      </section>

      {/* Premium Footer */}
      <PremiumFooter variant="light" />

      <Toaster />
    </div>
  );
}

export default App;
